import React, { useRef, useState, useCallback, useEffect } from 'react'
import { capitalizeMyText } from '../../utils/helpers'
import { useSelector } from 'react-redux'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import Icon from 'icons/Icon'
import isEmpty from 'lodash/isEmpty'

const GameCategories = ({
  selectedFilter,
  setSelectedFilter,
  gameSubCategory,
  selectedLanguageCode,
  hideAllGamesTab = false,
  selectedMenu
}) => {
  const { brandLogo, languageData } = useSelector(state => state.language)
  const { userDetails } = useSelector(state => state.user)
  const isLoggedIn = !isEmpty(userDetails)
  const uiConfig = useSelector(state => state?.uiConfig?.general)
  const gameCategoryExtraTabs = isLoggedIn ? (uiConfig?.gameCategoryExtraTabs || []) : []
  const spaceBetween = uiConfig?.gameSliderOptions?.gameCategorySliderGap ?? 20
  const showAllGamesTab = hideAllGamesTab ? false : (uiConfig?.showAllGamesTab ?? true)
  const swiperRef = useRef(null)
  const hasSlidToInitialCategory = useRef(false)
  const [isSwiperReady, setIsSwiperReady] = useState(false)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(true)

  const updateArrows = useCallback((swiper) => {
    if (!swiper) return
    if (!swiper.el) return
    setShowLeftArrow(!swiper.isBeginning)
    setShowRightArrow(!swiper.isEnd)
  }, [])

  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev()
    }
  }

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext()
    }
  }

  // Move to the initially active category once Swiper and categories are ready.
  useEffect(() => {
    if (hasSlidToInitialCategory.current || !isSwiperReady) return

    const swiper = swiperRef.current
    const subCategories = gameSubCategory?.rows?.filter(subCategory => !subCategory?.isLabeledCategory) || []
    const categorySlides = [
      ...subCategories.map(subCategory => subCategory?.id),
      ...gameCategoryExtraTabs.map(tab => tab?.key)
    ]
    const activeCategoryKey = selectedFilter?.subCategoryId || selectedFilter?.category?.[0] || selectedMenu
    if (!swiper || !subCategories.length || !activeCategoryKey) return

    const activeIndex = categorySlides.findIndex(categoryKey => categoryKey === activeCategoryKey)
    if (activeIndex < 0) return

    swiper.slideTo(activeIndex + (showAllGamesTab ? 1 : 0))
    hasSlidToInitialCategory.current = true
  }, [isSwiperReady, selectedFilter?.subCategoryId, selectedFilter?.category, selectedMenu, gameSubCategory?.rows, gameCategoryExtraTabs, showAllGamesTab])

  return (
    <>
      <div className={`game-categories-wrapper${showLeftArrow ? ' show-left-fade' : ''}${showRightArrow ? ' show-right-fade' : ''}`}>
        <button
          className={`category-scroll-arrow category-scroll-arrow--left${showLeftArrow ? ' visible' : ''}`}
          onClick={handlePrev}
          aria-label='Scroll left'
        >
          <Icon name='drop-left-arrow' />
        </button>

        <Swiper
          slidesPerView='auto'
          slidesPerGroup={3}
          spaceBetween={spaceBetween}
          className='mySwiper'
          direction='horizontal'
          loop={false}
          onSwiper={(swiper) => {
            swiperRef.current = swiper
            setIsSwiperReady(true)
            updateArrows(swiper)
          }}
          onSlideChange={(swiper) => updateArrows(swiper)}
          onReachBeginning={(swiper) => updateArrows(swiper)}
          onReachEnd={(swiper) => updateArrows(swiper)}
          onProgress={(swiper) => updateArrows(swiper)}
        >
          {showAllGamesTab && (
            <SwiperSlide>
              <a
                className={selectedFilter?.subCategoryId ? 'game-type' : 'game-type active'}
                onClick={() => {
                  // Keep the same object when Lobby is already active with no
                  // filters, so fresh empty arrays don't re-trigger the games fetch.
                  setSelectedFilter(selectedFilter => (
                    !selectedFilter?.subCategoryId &&
                    !selectedFilter?.category?.length &&
                    !selectedFilter?.provider?.length &&
                    !selectedFilter?.studio?.length &&
                    !selectedFilter?.rating?.length
                      ? selectedFilter
                      : {
                          ...selectedFilter,
                          category: [],
                          provider: [],
                          studio: [],
                          rating: [],
                          subCategoryId: null
                        }))
                }}
              >
                <Icon name='grid-2-solid' className='category-icon' />
                <span className='text'>
                  {capitalizeMyText(languageData?.all, '_')}
                </span>
              </a>
            </SwiperSlide>
          )}
          {gameSubCategory && gameSubCategory?.rows?.map((subCategory, index) => {
            if (subCategory?.isLabeledCategory) return null
            const subCategoryId = subCategory?.id
            const isCategoryActive = subCategoryId === selectedMenu || selectedFilter?.subCategoryId === subCategoryId

            return (
              <SwiperSlide key={index}>
                <a
                  className={isCategoryActive ? 'game-type active' : 'game-type'}
                  onClick={() => {
                    setSelectedFilter(selectedFilter => ({
                      ...selectedFilter,
                      category: [subCategoryId],
                      subCategoryId
                    }))
                  }}
                >
                  {subCategory?.thumbnailUrl
                    ? (
                      <img
                        src={subCategory?.thumbnailUrl || brandLogo || '/images/brand-logo.svg'}
                        onError={(e) => {
                          e.target.onerror = null
                          e.target.src = brandLogo || '/images/brand-logo.svg'
                        }}
                        alt='subCategoryImage'
                        className='category-icon'
                      />
                      )
                    : (
                      <Icon name='dice-solid' className='category-icon' />
                      )}

                  <span className='text'>
                    {capitalizeMyText((subCategory?.name?.[selectedLanguageCode] || subCategory?.name?.EN), '_')}
                  </span>
                </a>
              </SwiperSlide>
            )
          })}
          {gameCategoryExtraTabs.map((tab) => (
            <SwiperSlide key={tab.key}>
              <a
                className={selectedFilter?.category?.[0] === tab.key ? 'game-type active' : 'game-type'}
                onClick={() => {
                  setSelectedFilter(selectedFilter => ({
                    ...selectedFilter,
                    category: [tab.key],
                    provider: [],
                    studio: [],
                    rating: [],
                    subCategoryId: tab.key
                  }))
                }}
              >
                <Icon name={tab.icon} className='category-icon' />
                <span className='text'>
                  {capitalizeMyText(languageData?.[tab.labelKey] || tab.key, '_')}
                </span>
              </a>
            </SwiperSlide>
          ))}
        </Swiper>

        <button
          className={`category-scroll-arrow category-scroll-arrow--right${showRightArrow ? ' visible' : ''}`}
          onClick={handleNext}
          aria-label='Scroll right'
        >
          <Icon name='drop-right-arrow' />
        </button>
      </div>
    </>
  )
}

export default GameCategories
